import React, { ElementType, Fragment, ReactNode } from 'react';
import classNames from 'classnames';
import Box from '../Box';
import Inline from '../Inline';
import PaginationItem, { PaginationItemProps } from './PaginationItem';
import { getResponsiveValue, ResponsiveValue } from '../_internal/styling';
import useScreenSize from '../_internal/useScreenSize';
import { MarginProps } from '../_internal/spacing';
import { getItems, Length } from './utils';

import styles from './pagination.module.scss';

interface Props {
  /**
   * An accessibility label to make clear the purpose of the pagination.
   * It needs to be unique on the page.
   */
  'aria-label': string;
  /**
   * The color type of the pagination items
   *
   * @default 'default'
   */
  color?: PaginationItemProps['color'];
  /**
   * Whether or not the pagination component is disabled
   *
   * @default false
   */
  disabled?: PaginationItemProps['disabled'];
  /**
   * Size of the pagination items. This value is responsive and may be configured for multiple screen sizes.
   */
  itemSize?: PaginationItemProps['size'];
  /**
   * Callback called when the page changes
   */
  onChange?: (page: number) => void;
  /**
   * Render prop used to provide an alternate UI for pagination items like rendering links instead of buttons.
   */
  renderItem?: <C extends ElementType = 'button'>(
    item: PaginationItemProps<C>,
  ) => ReactNode;
  /**
   * Total number of pages in the pagination range
   */
  total: number;
  /**
   * Currently selected page. The first page is `1`.
   *
   * @default 1
   */
  value?: number;
  /**
   * The length of the pagination component, controlling the number of
   * pagination items that are visible at a time. Hidden pages are collapsed behind ellipses. This value is responsive and may be configured for multiple screen sizes.
   */
  visibleLength?: ResponsiveValue<Length>;
}

export type PaginationProps = Props & MarginProps;

/**
 * The pagination component enables users to select a specific page from a range of pages
 */
const Pagination = ({
  'aria-label': ariaLabel,
  color = 'default',
  disabled = false,
  visibleLength = { sm: 'short', md: 'medium', lg: 'long' },
  m,
  mt,
  mb,
  my,
  ml,
  mr,
  mx,
  onChange,
  value: selectedPage = 1,
  renderItem = item => <PaginationItem {...item} />,
  itemSize = { sm: 'small', md: 'large' },
  total,
}: PaginationProps) => {
  const screenSize = useScreenSize();
  const responsiveItemSize = getResponsiveValue(screenSize, itemSize, 'large');
  const paginationItems = getItems({
    length: getResponsiveValue(screenSize, visibleLength, 'short'),
    currentPage: selectedPage,
    itemSize: responsiveItemSize,
    total,
  });

  if (!paginationItems.length) {
    return null;
  }

  const margin = { m, mt, mb, my, ml, mr, mx } as MarginProps;

  return (
    <Box
      as="nav"
      role="navigation"
      aria-label={ariaLabel}
      className={classNames({ [styles['is-disabled']]: disabled })}
      {...margin}
    >
      <Inline spacing={0.5} vAlign="center" as="ul">
        {paginationItems.map(item => {
          if (item.variant === 'page') {
            const handleChange = () => {
              // only call `onClick` when not clicking the selected page
              if (!item.isSelected) {
                onChange?.(item.page);
              }
            };

            return (
              <Fragment key={item.page}>
                {renderItem({
                  page: item.page,
                  variant: item.variant,
                  isSelected: item.isSelected,
                  'aria-label': item.isSelected
                    ? `Page ${item.page}`
                    : `Go to page ${item.page}`,
                  ...(item.isSelected ? { 'aria-current': 'page' } : {}),
                  onClick: handleChange,
                  children: item.page,
                  disabled,
                  color,
                  size: itemSize,
                })}
              </Fragment>
            );
          }

          if (item.variant === 'previous' || item.variant === 'next') {
            return (
              <Fragment key={item.variant}>
                {renderItem({
                  page: item.page,
                  variant: item.variant,
                  'aria-label': `Go to the ${item.variant} page`,
                  disabled: disabled || item.disabled,
                  color,
                  size: responsiveItemSize,
                  onClick: () => {
                    if (item.page) {
                      onChange?.(item.page);
                    }
                  },
                })}
              </Fragment>
            );
          }

          if (
            item.variant === 'before-ellipsis' ||
            item.variant === 'after-ellipsis'
          ) {
            // we're not allowing customization of the ellipsis for now.
            // there is little to no benefit.
            return (
              <PaginationItem
                key={item.variant}
                variant={item.variant}
                aria-label={`Ellipsis for pages ${item.start} to ${item.end}`}
                color={color}
                disabled={disabled}
                size={itemSize}
              />
            );
          }

          return null;
        })}
      </Inline>
    </Box>
  );
};

export default Pagination;
